import React, { useEffect, useState, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  FaCalendarAlt,
  FaEye,
  FaFileAlt,
  FaList,
  FaMoneyBillAlt,
  FaTh,
  FaTrash,
  FaEdit,
  FaHospital,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import DashboardHeader from '../../components/DashboardHeader';
import { useAuth } from '../../context/AuthContext';
import '../../App.css';
import client from '../../api/client';
import { keepDecimalString, keepDigitsString ,keepDecimal2String} from '../../Utils/number';

function PostingByHospitals() {
  const { user } = useAuth();
  const [jobRequirements, setJobRequirements] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [hospitalNameFilter, setHospitalNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [professionOptions, setProfessionOptions] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [hospitalOptions, setHospitalOptions] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
  const [editingJobId, setEditingJobId] = useState(null);
  const [viewJobData, setViewJobData] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');
  const [viewingDetailsJobId, setViewingDetailsJobId] = useState(null);
  const [jobFormData, setJobFormData] = useState({
    hospitalID: '',
    lookingFor: '',
    qualification: '',
    experience: '',
    salary: '',
    startDate: '',
    endDate: '',
    startTime: '',
    duration: '',
    totalPerHours: '',
    shiftDetails: '',
    remarks: '',
    jobDetails: '',
    period: 'for a shift',
    suspend: false,
    jobStatus: 'Pending',
    activeStatus: true,
    jobID: null,
    publishedHourlyRate: "",
    publishedTotalAmount: "",

  });
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [isQualificationDisabled, setIsQualificationDisabled] = useState(true);
  const [viewType, setViewType] = useState('table');
  const [isPublishedChecked, setIsPublishedChecked] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState(null);
  const [professionalSearchTerm, setProfessionalSearchTerm] = useState('');
  const [isLoadingProfessionals, setIsLoadingProfessionals] = useState(false);
  const [showProfessionalDropdown, setShowProfessionalDropdown] = useState(false);
  const professionalDropdownRef = useRef(null);

  // --- unassign modal state ------------------------------------------------
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [unassignJob, setUnassignJob] = useState(null);
  const [unassignApplicationId, setUnassignApplicationId] = useState(null);
  const [selectedUnassignProfessionalId, setSelectedUnassignProfessionalId] = useState(null);
  const [confirmUnassign, setConfirmUnassign] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  // search/dropdown for unassign modal (mirrors view details but keeps open)
  const [unassignSearchTerm, setUnassignSearchTerm] = useState('');
  const [unassignShowDropdown, setUnassignShowDropdown] = useState(false);
  const unassignDropdownRef = useRef(null);
  const [unassignPublished, setUnassignPublished] = useState(false);

  const validateApprove = (job) => {
    if (!job) return "Job data not loaded";
  
    if (!isPublishedChecked) {
      return "Please mark job as Published before approving.";
    }
  
    const isShift = job.jobDurationType === "for a shift";
  
    if (isShift) {
      if (!job.publishedHourlyRate || Number(job.publishedHourlyRate) <= 0) {
        return "Published Hourly Rate is required for shift jobs.";
      }
      if (!job.publishedPeriodTotalAmount || Number(job.publishedPeriodTotalAmount) <= 0) {
        return "Published Total Amount is required for shift jobs.";
      }
    } else {
      if (!job.publishedPeriodTotalAmount || Number(job.publishedPeriodTotalAmount) <= 0) {
        return "Published Total Amount is required for period jobs.";
      }
    }
  
    return null; // ✅ valid
  };
  
  const handleApproveJob = async (jobId, status = "Open") => {
    if (!viewJobData) return;
    if(status === "Open"){
    const validationError = validateApprove(viewJobData);
    if (validationError) {
      toast.error(validationError);
      return;
    }
  }
    const payload = {
      jobID: jobId,
      jobStatus: status,
      activeStatus: true,
      isPublished: isPublishedChecked,
  
      // ✅ send published values from backend response (not editable here)
      publishedHourlyRate: viewJobData.publishedHourlyRate ?? null,
      publishedPeriodTotalAmount: viewJobData.publishedPeriodTotalAmount ?? null,
      
      // ✅ Include selected professional ID if selected (optional)
      professionalId: selectedProfessionalId || null,
    };
  
    try {
      await client.put("/api/joblistings/approve", payload);
      toast.success("Job Approved Successfully!");
      handleCloseViewDetailsModal();
      fetchJobListings();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to approve job!");
    }
  };
  
  
  const recordsPerPage = viewType === 'grid' ? 9 : 10;

  const getProfessionName = (id) => {
    const p = professionOptions.find((x) => Number(x.idProfessionType) === Number(id));
    return p ? p.professionTypeName : '-';
  };

  const getHospitalName = (id) => {
    const h = hospitalOptions.find((x) => Number(x.hospitalID) === Number(id));
    return h ? h.hospitalName : '-';
  };

  // return a human-readable job summary for modals
  const getJobSummary = (job) => {
    if (!job) return '';
    // if backend already returns a period string, use it
    if (job.period && job.period.trim() !== '') {
      return job.period;
    }
    // format date/time/duration similar to professional dashboard
    const parts = [];
    if (job.dateFrom) parts.push(formatDate(job.dateFrom));
    else if (job.createdAt) parts.push(formatDate(job.createdAt));

    if (job.timeFrom) parts.push(job.timeFrom);

    if (job.durationInHours) parts.push(`Duration ${job.durationInHours}.00 Hrs`);

    return parts.join(' - ');
  };

  const getQualificationName = (idProfession, idQualification) => {
    const q = degrees.find(
      (x) =>
        Number(x.idProfessional) === Number(idProfession) &&
        Number(x.idProfessionalDegree) === Number(idQualification),
    );
    return q ? q.professionalDegreeName : '-';
  };

  useEffect(() => {
    client
      .get('/api/professional-types')
      .then((response) => {
        const list = response.data?.data || [];
        setProfessionOptions(list);
        const doctor = list.find((p) => (p.professionTypeName || '').toLowerCase() === 'doctor');
        setJobFormData((prev) => ({
          ...prev,
          lookingFor: prev.jobID ? prev.lookingFor : doctor?.idProfessionType || '',
        }));
      })
      .catch(() => {
        setProfessionOptions([]);
      });
  }, []);

  useEffect(() => {
    client
      .get('/api/professional-degrees')
      .then((response) => {
        setDegrees(response.data?.data || []);
      })
      .catch(() => {
        setDegrees([]);
      });
  }, []);

  useEffect(() => {
    client
      .get('/api/hospitals/list')
      .then((response) => {
        const list = response.data?.data || [];
        setHospitalOptions(list);
        if (!jobFormData.hospitalID && list.length > 0) {
          setJobFormData((prev) => ({ ...prev, hospitalID: list[0].hospitalID }));
        }
      })
      .catch(() => setHospitalOptions([]));
  }, []);

  useEffect(() => {
    const filtered = degrees
      .filter((deg) => Number(deg.idProfessional) === Number(jobFormData.lookingFor))
      .map((deg) => ({ id: deg.idProfessionalDegree, name: deg.professionalDegreeName }));

    setQualificationOptions(filtered);
    setIsQualificationDisabled(filtered.length === 0);

    // If no qualifications available, clear selection
    if (filtered.length === 0 && jobFormData.qualification) {
      setJobFormData((prev) => ({ ...prev, qualification: '' }));
    }
  }, [jobFormData.lookingFor, degrees, jobFormData.qualification]);

  const fetchJobListings = async (fromDateParam = '') => {
    setIsLoading(true);
    setLoadError('');
    try {
      const params = {};
      if (fromDateParam) params.dateFrom = fromDateParam;

      const response = await client.get('/api/joblistings/list', { params });
      const list = response.data?.data || [];
      setJobRequirements(list);
      setCurrentPage(1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || 'Unable to load job listings.');
      setJobRequirements([]);
      setFilteredData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'assigned':      
        return 'badge bg-success';     
      case 'open':
        return 'badge bg-primary';
      case 'suspended':      
        return 'badge bg-warning text-dark';
      case 'rejected':
      case 'expired':
        return 'badge bg-danger text-dark';
      case 'deleted':
        return 'badge bg-danger text-dark';
      case 'completed':
        return 'badge bg-info text-dark';
      default:
        return 'badge bg-secondary';
    }
  };
  const formatINRNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(String(value).replace(/,/g, ''));
    if (Number.isNaN(num)) return '';
  
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };
  
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleEditJob = async (jobId) => {
    setEditingJobId(jobId);
    try {
      const response = await client.get(`/api/joblistings/${jobId}`);
      const data = response.data?.data;
      if (!data) throw new Error('Job not found');

      const isShift = data.jobDurationType === 'for a shift';
      const dateFromValue = data.dateFrom ? data.dateFrom.split('T')[0] : '';
      const dateToValue = data.dateTo ? data.dateTo.split('T')[0] : '';
      const timeFrom = data.timeFrom ? data.timeFrom.substring(0, 5) : '';

      setJobFormData((prev) => ({
        ...prev,
        jobID: data.jobID ?? jobId,
        hospitalID: data.hospitalID || '',
        lookingFor: data.idProfessionType || '',
        qualification: data.idQualification || '',
        experience: data.experienceInYears ?? '',
        salary: data.hourlyRateByHospital ?? '',
        startDate: dateFromValue,
        endDate: isShift ? '' : dateToValue,
        startTime: isShift ? timeFrom : '',
        duration: isShift ? data.durationInHours ?? '' : '',
        totalPerHours:  data.periodTotalAmountByHospital ?? '',
        period: isShift ? 'for a shift' : 'for a period',
        remarks: data.remarks || '',
        jobStatus: data.jobStatus || 'Pending',
        activeStatus: data.activeStatus ?? true,
        publishedHourlyRate: data.publishedHourlyRate ?? "",
        publishedTotalAmount: data.publishedPeriodTotalAmount ?? "",
      }));

      setShowEditJobModal(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load job details.');
      setEditingJobId(null);
    }
  };

  const fetchProfessionals = async (idProfessionType) => {
    if (!idProfessionType) {
      setProfessionals([]);
      return;
    }
    setIsLoadingProfessionals(true);
    try {
      const response = await client.get(`/api/healthcare-professionals/professionals/by-profession/${idProfessionType}`);
      const list = response.data?.data || [];
      setProfessionals(list);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load professionals.');
      setProfessionals([]);
    } finally {
      setIsLoadingProfessionals(false);
    }
  };

  const handleViewJobDetails = async (jobId) => {
    setViewingDetailsJobId(jobId);
    setIsViewLoading(true);
    setViewError('');
    setViewJobData(null);
    setSelectedProfessionalId(null);
    setProfessionalSearchTerm('');
    setShowViewDetailsModal(true);
    try {
      const response = await client.get(`/api/joblistings/${jobId}`);
      const data = response.data?.data;      
      if (!data) throw new Error('Job not found');
      
      setIsPublishedChecked(data.isPublished ?? false);
      setViewJobData(data);
      
      // Fetch professionals for this profession type
      if (data.idProfessionType) {
        await fetchProfessionals(data.idProfessionType);
        
        // ✅ Pre-select professional if IdProfessionalSelected exists and > 0
        if (data.idProfessionalSelected && Number(data.idProfessionalSelected) > 0) {
          setSelectedProfessionalId(data.idProfessionalSelected);
        }
      }
    } catch (err) {
      setViewError(err?.response?.data?.message || 'Unable to load job details.');
    } finally {
      setIsViewLoading(false);
    }
  };

  const handleCloseViewDetailsModal = () => {
    setShowViewDetailsModal(false);
    setViewingDetailsJobId(null);
    setViewJobData(null);
    setViewError('');
    setIsViewLoading(false);
    setSelectedProfessionalId(null);
    setProfessionalSearchTerm('');
    setProfessionals([]);
    setShowProfessionalDropdown(false);
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job requirement?')) {
      return;
    }

    try {
      const response = await client.delete(`/api/joblistings/${jobId}`, {
        headers: { accept: 'text/plain' },
      });

      if (response.status === 200) {
        toast.success('Job requirement deleted successfully!');
        fetchJobListings();
      } else {
        toast.error('Failed to delete job. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to delete job. Please try again.');
    }
  };

  // --- unassign helpers ----------------------------------------------------
  const openUnassignModal = async (job) => {
    // ensure we have latest data including applicationID
    let jobData = job;
    try {
      const resp = await client.get(`/api/joblistings/${job.jobID}`);
      if (resp.data?.data) {
        jobData = resp.data.data;
      }
    } catch (_) {
      // ignore network errors, continue with passed job
    }

    setUnassignJob(jobData);
    setUnassignApplicationId(jobData.applicationID || null);
    setSelectedUnassignProfessionalId(null);
    setConfirmUnassign(false);
    setUnassignSearchTerm('');
    setUnassignShowDropdown(false);
    setUnassignPublished(jobData.isPublished ?? false);

    // fetch professionals for this job's profession type (so admin can optionally assign new)
    if (jobData.idProfessionType) {
      fetchProfessionals(jobData.idProfessionType);
    } else {
      setProfessionals([]);
    }

    setShowUnassignModal(true);
  };

  const handleCloseUnassignModal = () => {
    setShowUnassignModal(false);
    setUnassignJob(null);
    setUnassignApplicationId(null);
    setSelectedUnassignProfessionalId(null);
    setConfirmUnassign(false);
  };

  const handleUnassignSubmit = async () => {
    if (!confirmUnassign) {
      toast.error('Please confirm the unassignment before saving.');
      return;
    }

    if (!unassignJob) return;

    setIsUnassigning(true);
    try {
      const payload = {
        jobID: unassignJob.jobID || unassignJob.id,
        applicationID: unassignApplicationId || null,
        professionalId: selectedUnassignProfessionalId || null,
        isPublished: unassignPublished,
      };

      const response = await client.post('/api/joblistings/unassign', payload);

      if (response.data?.success) {
        toast.success(response.data?.message || 'Job unassigned successfully!');
        fetchJobListings();
        handleCloseUnassignModal();
      } else {
        toast.error(response.data?.message || 'Failed to unassign job.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to unassign job. Please try again.');
    } finally {
      setIsUnassigning(false);
    }
  };


  const handleAddJobClick = () => {
    const doctor = professionOptions.find((p) => (p.professionTypeName || '').toLowerCase() === 'doctor');
    const firstHospital = hospitalOptions[0];

    setJobFormData({
      hospitalID: '',
      lookingFor: doctor ? doctor.idProfessionType : '',
      qualification: '',
      experience: '',
      salary: '',
      startDate: '',
      endDate: '',
      startTime: '',
      duration: '',
      totalPerHours: '',
      shiftDetails: '',
      remarks: '',
      jobDetails: '',
      period: 'for a shift',
      suspend: false,
      jobStatus: 'Pending',
      activeStatus: true,
      jobID: null,
    });

    setShowAddJobModal(true);
  };
  const getMinStartTime = () => {
    if (!jobFormData.startDate) return '';

    const today = new Date().toISOString().split('T')[0];
    if (jobFormData.startDate !== today) return '00:00'; // future date => any time ok

    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`; // today => now or later
  };

  const handleJobFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setJobFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (['lookingFor', 'qualification', 'hospitalID'].includes(name)) {
      const numericVal = value === '' ? '' : Number(value);
      setJobFormData((prev) => ({
        ...prev,
        [name]: numericVal,
        ...(name === 'lookingFor' ? { qualification: '' } : {}),
      }));
      return;
    }
    // Decimal fields: salary, duration, totalPerHours (allow free decimals + auto-calc total for shift)
    if (
      name === "salary" ||
      name === "duration" ||
      name === "totalPerHours" ||
      name === "publishedHourlyRate" ||
      name === "publishedTotalAmount"
    ) {
      const cleaned = keepDecimal2String(value);
    
      setJobFormData((prev) => {
        const updated = { ...prev, [name]: cleaned };
    
        // ✅ Add Modal Shift Auto calc: totalPerHours = salary * duration
        if (updated.period === "for a shift") {
          const hourly = parseFloat(updated.salary);
          const dur = parseFloat(updated.duration);
    
          updated.totalPerHours =
            !Number.isNaN(hourly) && !Number.isNaN(dur) && hourly > 0 && dur > 0
              ? (hourly * dur).toFixed(2)
              : "";
    
          // ✅ Edit Modal Shift Auto calc: publishedTotalAmount = publishedHourlyRate * duration
          const pubHourly = parseFloat(updated.publishedHourlyRate);
          updated.publishedTotalAmount =
            !Number.isNaN(pubHourly) && !Number.isNaN(dur) && pubHourly > 0 && dur > 0
              ? (pubHourly * dur).toFixed(2)
              : "";
        }
    
        return updated;
      });
    
      return;
    }
    

    // Experience: digits only, clamped 0–15
    if (name === 'experience') {
      const digits = keepDigitsString(value);
      if (digits === '') {
        setJobFormData((prev) => ({ ...prev, experience: '' }));
        return;
      }
      let numValue = parseInt(digits, 10);
      if (Number.isNaN(numValue) || numValue < 0) {
        numValue = 0;
      }
      if (numValue > 15) numValue = 15;
      setJobFormData((prev) => ({ ...prev, experience: numValue }));
      return;
    }   

    if (name === 'shiftDetails') {
      setJobFormData((prev) => ({ ...prev, [name]: value.slice(0, 100) }));
      return;
    }

    if (name === 'remarks') {
      setJobFormData((prev) => ({ ...prev, [name]: value.slice(0, 1000) }));
      return;
    }

    setJobFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleJobFormSubmit = async (e) => {
    e.preventDefault();

    const isShift = jobFormData.period === 'for a shift';

    if (!jobFormData.hospitalID || !jobFormData.lookingFor || !jobFormData.qualification || !jobFormData.startDate) {
      toast.error('Please fill in all required fields!');
      return;
    }

    if (!isShift && !jobFormData.endDate) {
      toast.error('End date is required for a period.');
      return;
    }

    const experience = Number(jobFormData.experience);
    if (jobFormData.experience !== '' && (Number.isNaN(experience) || experience < 0 || experience > 15)) {
      toast.error('Average Years of Experience must be between 0 and 15!');
      return;
    }

    if (isShift) {
      // ✅ Start Time must be current/future time when Start Date is today
      const today = new Date().toISOString().split('T')[0];

      if (jobFormData.startDate === today) {
        if (!jobFormData.startTime) {
          toast.error('Start Time is required.');
          return;
        }

        const now = new Date();
        const [h, m] = jobFormData.startTime.split(':').map(Number);

        const selected = new Date();
        selected.setSeconds(0, 0);
        selected.setHours(h, m, 0, 0);

        if (selected < now) {
          toast.error('Start Time must be current time or a future time.');
          return;
        }
      }

      // existing validations
      const duration = Number(jobFormData.duration);
      const hourly = Number(jobFormData.salary);

      if (Number.isNaN(duration) || duration <= 0) {
        toast.error('Duration must be greater than 0.');
        return;
      }
      if (Number.isNaN(hourly) || hourly <= 0) {
        toast.error('Hourly amount must be greater than 0.');
        return;
      }
    } else {
      const total = Number(jobFormData.totalPerHours);
      if (Number.isNaN(total) || total <= 0) {
        toast.error('Total amount must be greater than 0 for a period.');
        return;
      }
    }

    const formatTime = (t) => (t ? `${t}:00` : null);
    
    const payload = {
      idProfessionType: jobFormData.lookingFor || null,
      hospitalID: jobFormData.hospitalID || null,
      idQualification: jobFormData.qualification || null,
      experienceInYears: jobFormData.experience === '' ? null : Number(jobFormData.experience),
      jobDurationType: isShift ? 'for a shift' : 'for a period',
      dateFrom: jobFormData.startDate || null,
      dateTo: isShift ? null : jobFormData.endDate || null,
      timeFrom: isShift ? formatTime(jobFormData.startTime) : null,
      durationInHours: isShift ? (jobFormData.duration === '' ? null : Number(jobFormData.duration)) : null,
      hourlyRateByHospital: isShift ? (jobFormData.salary === '' ? null : Number(jobFormData.salary)) : 0,
    
      // ✅ IMPORTANT FIX
      periodTotalAmountByHospital:
        jobFormData.totalPerHours === '' ? null : Number(jobFormData.totalPerHours),
    
      jobStatus: 'Pending',
      activeStatus: true,
      remarks: jobFormData.remarks || '',
    };

    try {
      await client.post('/api/joblistings', payload);
      toast.success('Job requirement added successfully!');
      handleCloseModal();
      fetchJobListings();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add job requirement.');
    }
  };

  const handleCloseModal = () => {
    setShowAddJobModal(false);
    setShowEditJobModal(false);
    setEditingJobId(null);
    setJobFormData({
      hospitalID: hospitalOptions[0]?.hospitalID || '',
      lookingFor: '',
      qualification: '',
      experience: '',
      salary: '',
      startDate: '',
      endDate: '',
      startTime: '',
      duration: '',
      totalPerHours: '',
      shiftDetails: '',
      remarks: '',
      jobDetails: '',
      period: 'for a shift',
      suspend: false,
      jobStatus: 'Pending',
      activeStatus: true,
    });
    setQualificationOptions([]);
    setIsQualificationDisabled(true);
  };

  const handleEditJobFormSubmit = async (e) => {
    e.preventDefault();

    const isShift = jobFormData.period === 'for a shift';

    if (!jobFormData.hospitalID || !jobFormData.lookingFor || !jobFormData.qualification || !jobFormData.startDate) {
      toast.error('Please fill in all required fields!');
      return;
    }

    if (!isShift && !jobFormData.endDate) {
      toast.error('End date is required for a period.');
      return;
    }

    const experience = Number(jobFormData.experience);
    if (jobFormData.experience !== '' && (Number.isNaN(experience) || experience < 0 || experience > 15)) {
      toast.error('Average Years of Experience must be between 0 and 15!');
      return;
    }

    if (isShift) {
      const duration = Number(jobFormData.duration);
      const hourly = Number(jobFormData.salary);
      if (Number.isNaN(duration) || duration <= 0) {
        toast.error('Duration must be greater than 0.');
        return;
      }
      if (Number.isNaN(hourly) || hourly <= 0) {
        toast.error('Hourly amount must be greater than 0.');
        return;
      }
    } else {
      const total = Number(jobFormData.totalPerHours);
      if (Number.isNaN(total) || total <= 0) {
        toast.error('Total amount must be greater than 0 for a period.');
        return;
      }
    }

    const formatTime = (t) => (t ? `${t}:00` : null);
    const payload = {
      jobID: jobFormData.jobID ?? editingJobId,
      idProfessionType: jobFormData.lookingFor || null,
      hospitalID: jobFormData.hospitalID || null,
      idQualification: jobFormData.qualification || null,
      experienceInYears: jobFormData.experience === "" ? null : Number(jobFormData.experience),
    
      jobDurationType: isShift ? "for a shift" : "for a period",
      dateFrom: jobFormData.startDate || null,
      dateTo: isShift ? null : jobFormData.endDate || null,
      timeFrom: isShift ? formatTime(jobFormData.startTime) : null,
      durationInHours: isShift ? (jobFormData.duration === "" ? null : Number(jobFormData.duration)) : null,
      hourlyRateByHospital: isShift ? (jobFormData.salary === '' ? null : Number(jobFormData.salary)) : 0,
      periodTotalAmountByHospital: jobFormData.totalPerHours === '' ? null : Number(jobFormData.totalPerHours),
     
      publishedHourlyRate: isShift
        ? (jobFormData.publishedHourlyRate === "" ? null : Number(jobFormData.publishedHourlyRate))
        : 0,
    
      // ✅ IMPORTANT FIX
      publishedTotalAmount:
        jobFormData.publishedTotalAmount === "" ? null : Number(jobFormData.publishedTotalAmount),
    
      jobStatus: jobFormData.jobStatus || "Pending",
      activeStatus: jobFormData.activeStatus ?? true,
      remarks: jobFormData.remarks || "",
    };
    if (isShift) {
      if (!jobFormData.publishedHourlyRate) {
        toast.error("Published Hourly Rate is required!");
        return;
      }
      // ✅ Validate published hourly rate doesn't exceed total amount
      const publishedTotal = Number(jobFormData.publishedTotalAmount);
      const totalAmount = Number(jobFormData.totalPerHours);
      if (publishedTotal > totalAmount) {
        toast.error("Published Total Amount cannot be greater than Total Amount. (Loss for company)");
        return;
      }
    }
    if (!isShift) {
      if (!jobFormData.publishedTotalAmount) {
        toast.error("Published Total Amount is required!");
        return;
      }      
      // ✅ Validate published total amount doesn't exceed total amount
      const publishedTotal = Number(jobFormData.publishedTotalAmount);
      const totalAmount = Number(jobFormData.totalPerHours);
      if (publishedTotal > totalAmount) {
        toast.error("Published Total Amount cannot be greater than Total Amount. (Loss for company)");
        return;
      }
    }   

    try {
      await client.put('/api/joblistings', payload);
      toast.success('Job requirement updated successfully!');
      handleCloseModal();
      fetchJobListings();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update job requirement.');
    }
  };

  useEffect(() => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() - 90);
    setDateFrom('');
    fetchJobListings('');
  }, []);

  // Refetch when date filter changes (server-side filter)
  useEffect(() => {
    fetchJobListings(dateFrom);
  }, [dateFrom]);

  useEffect(() => {
    const filtered = jobRequirements.filter((job) => {
      const hospitalName = (job.hospitalName || getHospitalName(job.hospitalID) || '').toLowerCase();
      const professionName = (job.professionTypeName || getProfessionName(job.idProfessionType) || '').toLowerCase();
      const status = (job.jobStatus || job.status || '').toLowerCase();

      // Hospital text filter
      if (hospitalNameFilter && !hospitalName.includes(hospitalNameFilter.toLowerCase())) return false;

      // Search filter across hospital/profession
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!hospitalName.includes(term) && !professionName.includes(term)) return false;
      }

      // Date from filter
      if (dateFrom) {
        const createdAt = job.createdAt ? new Date(job.createdAt) : null;
        const from = new Date(dateFrom);
        if (!createdAt || createdAt < from) return false;
      }

      // Status filter
      const desiredStatus = (statusFilter || 'All').toLowerCase();
      if (desiredStatus !== 'all' && status !== desiredStatus) return false;

      return true;
    });
    setFilteredData(filtered);
  }, [jobRequirements, searchTerm, hospitalNameFilter, hospitalOptions, dateFrom, statusFilter, professionOptions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [viewType]);

  // Close professional dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfessionalDropdown && professionalDropdownRef.current && !professionalDropdownRef.current.contains(event.target)) {
        setShowProfessionalDropdown(false);
      }
    };

    if (showProfessionalDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showProfessionalDropdown]);

  // Close unassign dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        unassignShowDropdown &&
        unassignDropdownRef.current &&
        !unassignDropdownRef.current.contains(event.target)
      ) {
        setUnassignShowDropdown(false);
      }
    };

    if (unassignShowDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [unassignShowDropdown]);

  const currentPageData = getCurrentPageData();

  return (
    <div className="App">
      <DashboardHeader userType="admin" userName={user?.displayName || 'Admin'} />

      <main className="dashboard-content">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="d-block d-md-flex justify-content-between align-items-center  mb-4">
                <h2 className="dashboard-title mb-2">Posting by Hospitals</h2>
                <div className="d-flex align-items-center gap-2">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewType('table')}
                      title="Table View"
                    >
                      <FaList />
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewType === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewType('grid')}
                      title="Grid View"
                    >
                      <FaTh />
                    </button>
                  </div>
                  <button type="button" className="btn btn-primary" onClick={handleAddJobClick}>
                    Add Job Requirement
                  </button>
                </div>
              </div>

              <div className="filter-section mb-4">

                <div className="row align-items-end">
                  <div className="col-md-4 col-lg-3">
                    <label htmlFor="hospitalNameFilter" className="form-label">
                      Hospital
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <FaHospital />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="hospitalNameFilter"
                        placeholder="Hospital Name"
                        value={hospitalNameFilter}
                        onChange={(e) => setHospitalNameFilter(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4 col-lg-3">
                    <label htmlFor="dateFrom" className="form-label">
                      Date From
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="dateFrom"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      placeholder="Select date"
                    />
                  </div>
                  <div className="col-md-4 col-lg-3">
                    <label htmlFor="statusFilter" className="form-label">
                      Status
                    </label>
                    <select
                      className="form-select"
                      id="statusFilter"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="All">All</option>  
                      <option value="Pending">Pending</option>               
                      <option value="Rejected">Rejected</option>
                      <option value="Expired">Expired</option>
                      <option value="Assigned">Assigned</option>
                      <option value="Completed">Completed</option>                      
                      <option value="Suspended">Suspended</option>
                   
                    </select>
                  </div>
                </div>
              </div>

              {viewType === 'table' && (
                <div className="card">
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Posted Date</th>
                            <th>Hospital</th>
                            <th>Requirement</th>
                            <th>Type</th>
                            <th>Job Details</th>
                            <th>Total Amount</th>
                            <th>Published Amount</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan="8" className="text-center">
                                Loading...
                              </td>
                            </tr>
                          ) : loadError ? (
                            <tr>
                              <td colSpan="8" className="text-center text-danger">
                                {loadError}
                              </td>
                            </tr>
                          ) : currentPageData.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="text-center">
                                No job requirements found
                              </td>
                            </tr>
                          ) : (
                            currentPageData.map((job) => (
                              <tr key={job.jobID}>
                                <td>{formatDate(job.createdAt)}</td>
                                <td>{job.hospitalName || getHospitalName(job.hospitalID)}</td>
                                <td>{job.professionTypeName || '-'}</td>
                                <td>{job.jobDurationType || '-'}</td>
                                <td>{job.jobDetails || '-'}</td>
                                <td  className="text-end">{job.totalAmount ? `Rs. ${formatINRNumber(job.totalAmount)}` : '-'}</td>
                                <td className="text-end">
                                  {(job.publishedPeriodTotalAmount !== null && job.publishedPeriodTotalAmount !== undefined && job.publishedPeriodTotalAmount !== '') || (job.publishedTotalAmount !== null && job.publishedTotalAmount !== undefined && job.publishedTotalAmount !== '')
                                    ? `Rs. ${formatINRNumber(job.publishedPeriodTotalAmount ?? job.publishedTotalAmount)}`
                                    : '-'}
                                </td>
                                <td>
                                  <span className={getStatusBadgeClass(job.jobStatus || '')}>{job.jobStatus || '-'}</span>
                                </td>
                                <td>
                                {job.jobStatus != 'Assigned' && job.jobStatus != 'Completed' && (
                                  <button
                                    type="button"
                                    className="btn btn-link btn-outline-primary p-1 px-2 me-1"
                                    onClick={() => {
                                      handleEditJob(job.jobID);
                                    }}
                                  >
                                    <FaEdit />
                                  </button>
                                    )}
                                  <button
                                    type="button"
                                    className="btn btn-link btn-outline-primary p-1 px-2 me-1"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleViewJobDetails(job.jobID);
                                    }}
                                    title="View Details"
                                  >
                                    <FaEye />
                                  </button>
                                  {job.jobStatus === 'Assigned' && (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-outline-danger p-1 px-2 me-1"
                                      title="Unassign Professional"
                                      onClick={() => openUnassignModal(job)}
                                    >
                                      <FaTimes />
                                    </button>
                                  )}
                                  {job.jobStatus === 'Pending' && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger p-1 px-2"
                                      onClick={() => handleDeleteJob(job.jobID)}
                                    >
                                      <FaTrash />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {viewType === 'grid' && (
                <div className="row g-3">
                  {isLoading ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-muted">Loading...</p>
                        </div>
                      </div>
                    </div>
                  ) : loadError ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-danger">{loadError}</p>
                        </div>
                      </div>
                    </div>
                  ) : currentPageData.length === 0 ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-muted">No job requirements found</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    currentPageData.map((job) => (
                      <div key={job.jobID} className="col-12 col-md-6 col-lg-4">
                        <div className="card h-100 opportunity-card modern-card">
                          <div className="card-body d-flex flex-column p-0">
                            <div className="card-header-section">
                              <h5 className="card-position-title">{job.professionTypeName || '-'}</h5>
                              <div className="d-flex align-items-center gap-2">
                                <span className={getStatusBadgeClass(job.jobStatus || '')}>{job.jobStatus || '-'}</span>
                              </div>
                            </div>

                            <div className="card-info-section">
                              <div className="info-item">
                                <div className="info-icon hospital-icon">
                                  <FaHospital />
                                </div>
                                <div className="info-content">
                                  <div className="info-label">Hospital</div>
                                  <div className="info-value">{job.hospitalName || getHospitalName(job.hospitalID)}</div>
                                </div>
                              </div>
                              <div className="info-item">
                                <div className="info-icon hospital-icon">
                                  <FaCalendarAlt />
                                </div>
                                <div className="info-content">
                                  <div className="info-label">Posted Date</div>
                                  <div className="info-value">{formatDate(job.createdAt)}</div>
                                </div>
                              </div>
                            </div>

                            <div className="card-divider"></div>

                            <div className="card-details-section">
                              <div className="detail-item">
                                <FaFileAlt className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Type</span>
                                  <span className="detail-value">{job.jobDurationType || '-'}</span>
                                </div>
                              </div>
                              <div className="detail-item">
                                <FaCalendarAlt className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Job Details</span>
                                  <span className="detail-value">{job.jobDetails || '-'}</span>
                                </div>
                              </div>
                              <div className="detail-item">
                                <FaMoneyBillAlt className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Total Amount</span>
                                  <span className="detail-value">
  {job.totalAmount ? `Rs. ${formatINRNumber(job.totalAmount)}` : '-'}
</span>
                                </div>
                              </div>

                              <div className="detail-item">
                                <FaMoneyBillAlt className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Published Amount</span>
                                  <span className="detail-value">
  {(job.publishedPeriodTotalAmount !== null && job.publishedPeriodTotalAmount !== undefined && job.publishedPeriodTotalAmount !== '') || (job.publishedTotalAmount !== null && job.publishedTotalAmount !== undefined && job.publishedTotalAmount !== '')
    ? `Rs. ${formatINRNumber(job.publishedPeriodTotalAmount ?? job.publishedTotalAmount)}`
    : '-'}
</span>
                                </div>
                              </div>
                              <div className="d-flex justify-content-end gap-1">
                                {job.jobStatus != 'Assigned' && job.jobStatus != 'Completed' && (
                                <button
                                  type="button"
                                  className="btn btn-link btn-outline-primary p-1 px-2"
                                  onClick={() => {
                                    handleEditJob(job.jobID);
                                  }}
                                  title="Edit"
                                >
                                  <FaEdit />
                                </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-link btn-outline-primary p-1 px-2"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleViewJobDetails(job.jobID);
                                  }}
                                  title="View Details"
                                >
                                  <FaEye />
                                </button>
                                {job.jobStatus === 'Assigned' && (
                                  <button
                                    type="button"
                                    className="btn btn-link btn-outline-danger p-1 px-2"
                                    title="Unassign Professional"
                                    onClick={() => openUnassignModal(job)}
                                  >
                                    <FaTimes />
                                  </button>
                                )}
                                {job.jobStatus === 'Pending' && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger p-1 px-2"
                                    onClick={() => {
                                      handleDeleteJob(job.jobID);
                                    }}
                                    title="Delete"
                                  >
                                    <FaTrash />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {totalPages > 1 && (
                <nav aria-label="Page navigation" className="mt-4">
                  <ul className="pagination justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <a
                        className="page-link"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                      >
                        Previous
                      </a>
                    </li>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                        <a
                          className="page-link"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(page);
                          }}
                        >
                          {page}
                        </a>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <a
                        className="page-link"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>
      </main>

      <Modal show={showAddJobModal} onHide={handleCloseModal} centered size="lg" scrollable backdrop="static"
        keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>Add Job Requirement</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleJobFormSubmit}>
          <Modal.Body>
            <div className="row m-0">
              <div className="col-md-6 p-2">
                <label htmlFor="hospitalID" className="form-label mb-1">
                  Hospital <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="hospitalID"
                  name="hospitalID"
                  value={jobFormData.hospitalID}
                  onChange={handleJobFormChange}
                  required
                >
                  <option value="">Select Hospital</option>
                  {hospitalOptions.map((h) => (
                    <option key={h.hospitalID} value={h.hospitalID}>
                      {h.hospitalName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="lookingFor" className="form-label mb-1">
                  Looking For <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="lookingFor"
                  name="lookingFor"
                  value={jobFormData.lookingFor}
                  onChange={handleJobFormChange}
                  required
                >
                  <option value="">Select Profession</option>
                  {professionOptions.map((p) => (
                    <option key={p.idProfessionType} value={p.idProfessionType}>
                      {p.professionTypeName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="qualification" className="form-label mb-1">
                  Qualification <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="qualification"
                  name="qualification"
                  value={jobFormData.qualification}
                  onChange={handleJobFormChange}
                  required
                  disabled={isQualificationDisabled}
                >
                  <option value="">Select Qualification</option>
                  {qualificationOptions.map((qual) => (
                    <option key={qual.id} value={qual.id}>
                      {qual.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="experience" className="form-label mb-1">
                  Average Years of Experience
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="experience"
                  name="experience"
                  value={jobFormData.experience}
                  onChange={handleJobFormChange}
                  placeholder="Enter years (0-15)"
                  min="0"
                  max="15"
                />
              </div>

              <div className="col-md-6 p-2">
                <label className="form-label mb-1">Period</label>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="period"
                    id="periodShift"
                    value="for a shift"
                    checked={jobFormData.period === 'for a shift'}
                    onChange={handleJobFormChange}
                  />
                  <label className="form-check-label" htmlFor="periodShift">
                    For a shift
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="period"
                    id="periodDay"
                    value="for a period"
                    checked={jobFormData.period === 'for a period'}
                    onChange={handleJobFormChange}
                  />
                  <label className="form-check-label" htmlFor="periodDay">
                    For a period
                  </label>
                </div>
              </div>

              <div className="col-md-3 p-2">
                <label htmlFor="editStartDate" className="form-label mb-1">
                  Start Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="editStartDate"
                  name="startDate"
                  value={jobFormData.startDate}
                  onChange={handleJobFormChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              {jobFormData.period === 'for a period' && (
                <div className="col-md-3 p-2">
                  <label htmlFor="editEndDate" className="form-label mb-1">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="editEndDate"
                    name="endDate"
                    value={jobFormData.endDate}
                    onChange={handleJobFormChange}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              )}

              {jobFormData.period === 'for a shift' && (
                <>
                  <div className="col-md-3 p-2">
                    <label htmlFor="editStartTime" className="form-label mb-1">
                      Start Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      id="editStartTime"
                      name="startTime"
                      value={jobFormData.startTime || ''}
                      onChange={handleJobFormChange}
                      min={getMinStartTime()}
                      required
                    />
                  </div>

                  <div className="col-md-3 p-2">
                    <label htmlFor="salary" className="form-label mb-1">
                      Hourly Amount
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="salary"
                      name="salary"
                      value={(jobFormData.salary)}
                      onChange={handleJobFormChange}
                      placeholder="Hourly amount (e.g., 500)"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="col-md-3 p-2">
                    <label htmlFor="editDuration" className="form-label mb-1">
                      Duration (in Hours)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="editDuration"
                      name="duration"
                      value={jobFormData.duration || ''}
                      onChange={handleJobFormChange}
                      min="0"
                      max="12"
                      step="0.25"
                      required
                    />
                  </div>
                </>
              )}

              <div className="col-md-6 p-2">
                <label htmlFor="edittotalPerHours" className="form-label mb-1">
                  Total Amount <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="edittotalPerHours"
                  name="totalPerHours"
                  value={jobFormData.totalPerHours || ""}
                  onChange={handleJobFormChange}
                  min="0"
                  step="0.01"
                  readOnly={jobFormData.period === 'for a shift'}
                  required
                />
                {jobFormData.period === 'for a shift' && (
                  <small className="form-text text-muted">Auto-calculated: hourly amount × duration</small>
                )}
              </div>

              <div className="col-md-12 p-2">
                <label className="form-label mb-1">Job details</label>
                <ReactQuill
                  theme="snow"
                  value={jobFormData.remarks}
                  className="experience-details-editor"
                  onChange={(value) => {
                    const textContent = value.replace(/<[^>]*>/g, '');
                    if (textContent.length <= 1000) {
                      setJobFormData((prev) => ({ ...prev, remarks: value }));
                    }
                  }}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['link'],
                      ['clean'],
                    ],
                  }}
                  formats={['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link']}
                  style={{ minHeight: '150px', marginBottom: '50px' }}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Add Requirement
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
  show={showEditJobModal}
  onHide={handleCloseModal}
  centered
  size="lg"
  scrollable
  backdrop="static"
  keyboard={false}
>
  <Modal.Header closeButton>
    <Modal.Title>Edit Job Requirement</Modal.Title>
  </Modal.Header>

  <form onSubmit={handleEditJobFormSubmit}>
    <Modal.Body>
      <div className="row m-0">

        {/* Hospital */}
        <div className="col-md-6 p-2">
          <label htmlFor="editHospitalID" className="form-label mb-1">
            Hospital <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            id="editHospitalID"
            name="hospitalID"
            value={jobFormData.hospitalID}
            onChange={handleJobFormChange}
            required
          >
            <option value="">Select Hospital</option>
            {hospitalOptions.map((h) => (
              <option key={h.hospitalID} value={h.hospitalID}>
                {h.hospitalName}
              </option>
            ))}
          </select>
        </div>

        {/* Looking For */}
        <div className="col-md-6 p-2">
          <label htmlFor="editLookingFor" className="form-label mb-1">
            Looking For <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            id="editLookingFor"
            name="lookingFor"
            value={jobFormData.lookingFor}
            onChange={handleJobFormChange}
            required
          >
            <option value="">Select Profession</option>
            {professionOptions.map((p) => (
              <option key={p.idProfessionType} value={p.idProfessionType}>
                {p.professionTypeName}
              </option>
            ))}
          </select>
        </div>

        {/* Qualification */}
        <div className="col-md-6 p-2">
          <label htmlFor="editQualification" className="form-label mb-1">
            Qualification <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            id="editQualification"
            name="qualification"
            value={jobFormData.qualification}
            onChange={handleJobFormChange}
            required
            disabled={isQualificationDisabled}
          >
            <option value="">Select Qualification</option>
            {qualificationOptions.map((qual) => (
              <option key={qual.id} value={qual.id}>
                {qual.name}
              </option>
            ))}
          </select>
        </div>

        {/* Experience */}
        <div className="col-md-6 p-2">
          <label htmlFor="editExperience" className="form-label mb-1">
            Average Years of Experience
          </label>
          <input
            type="number"
            className="form-control"
            id="editExperience"
            name="experience"
            value={jobFormData.experience}
            onChange={handleJobFormChange}
            placeholder="Enter years (0-15)"
            min="0"
            max="15"
          />
        </div>

        {/* Period (readonly) */}
        <div className="col-md-6 p-2">
          <label className="form-label mb-1">Period</label>
          <input
            type="text"
            className="form-control"
            value={jobFormData.period}
            readOnly
          />
        </div>

        {/* Start Date */}
        <div className="col-md-3 p-2">
          <label htmlFor="editStartDate" className="form-label mb-1">
            Start Date <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            className="form-control"
            id="editStartDate"
            name="startDate"
            value={jobFormData.startDate}
            onChange={handleJobFormChange}
            min={new Date().toISOString().split("T")[0]}
            required
          />
        </div>

        {/* End Date (only period) */}
        {jobFormData.period === "for a period" && (
          <div className="col-md-3 p-2">
            <label htmlFor="editEndDate" className="form-label mb-1">
              End Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              id="editEndDate"
              name="endDate"
              value={jobFormData.endDate}
              onChange={handleJobFormChange}
              min={new Date().toISOString().split("T")[0]}
              required
            />
          </div>
        )}

        {/* Shift Details */}
        {jobFormData.period === "for a shift" && (
          <>
            {/* Start Time */}
            <div className="col-md-3 p-2">
              <label htmlFor="editStartTime" className="form-label mb-1">
                Start Time <span className="text-danger">*</span>
              </label>
              <input
                type="time"
                className="form-control"
                id="editStartTime"
                name="startTime"
                value={jobFormData.startTime || ""}
                onChange={handleJobFormChange}
                required
              />
            </div>

            {/* Duration (readonly) */}
            <div className="col-md-3 p-2">
              <label className="form-label mb-1">Duration (Hours)</label>
              <input
                type="number"
                className="form-control"
                value={jobFormData.duration || ""}
                readOnly
              />
            </div>

            {/* Hourly Amount (readonly) */}
            <div className="col-md-3 p-2">
              <label className="form-label mb-1">Hourly Amount</label>
              <input
                type="number"
                className="form-control"
                value={jobFormData.salary || ""}
                readOnly
              />
            </div>

            {/* Total Amount (readonly) */}
            <div className="col-md-3 p-2">
              <label className="form-label mb-1">Total Amount</label>
              <input
                type="number"
                className="form-control"
                value={jobFormData.totalPerHours || ""}
                readOnly
              />
            </div>

            {/* ✅ Published Hourly Rate (user will type) */}
            <div className="col-md-3 p-2">
              <label htmlFor="publishedHourlyRate" className="form-label mb-1">
                Published Hourly Rate <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                id="publishedHourlyRate"
                name="publishedHourlyRate"
                value={jobFormData.publishedHourlyRate || ""}
                onChange={handleJobFormChange}
                min="0"
                step="0.01"
                required
              />
            </div>

            {/* ✅ Published Total Amount (auto-calculated) */}
            <div className="col-md-3 p-2">
              <label htmlFor="publishedTotalAmount" className="form-label mb-1">
                Published Total Amount <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                id="publishedTotalAmount"
                name="publishedTotalAmount"
                value={jobFormData.publishedTotalAmount || ""}
                readOnly
                min="0"
                step="0.01"
                required
              />
              <small className="text-muted">
                Auto-calculated: Published Hourly Rate × Duration
              </small>
            </div>
          </>
        )}

        {/* Period Published Total Amount */}
        {jobFormData.period === "for a period" && (
          <>
            {/* Old Total Amount (readonly) */}
            <div className="col-md-6 p-2">
              <label className="form-label mb-1">Total Amount</label>
              <input
                type="number"
                className="form-control"
                value={jobFormData.totalPerHours || ""}
                readOnly
              />
            </div>

            {/* ✅ Published Total Amount (user types) */}
            <div className="col-md-6 p-2">
              <label htmlFor="publishedTotalAmount" className="form-label mb-1">
                Published Total Amount <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                id="publishedTotalAmount"
                name="publishedTotalAmount"
                value={jobFormData.publishedTotalAmount || ""}
                onChange={handleJobFormChange}
                min="0"
                step="0.01"
                required
              />
            </div>
          </>
        )}

        {/* Job Details */}
        <div className="col-md-12 p-2">
          <label className="form-label mb-1">Job details</label>
          <ReactQuill
            theme="snow"
            value={jobFormData.remarks}
            className="experience-details-editor"
            onChange={(value) => {
              const textContent = value.replace(/<[^>]*>/g, "");
              if (textContent.length <= 1000) {
                setJobFormData((prev) => ({ ...prev, remarks: value }));
              }
            }}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link"],
                ["clean"],
              ],
            }}
            formats={[
              "header",
              "bold",
              "italic",
              "underline",
              "strike",
              "list",
              "bullet",
              "link",
            ]}
            style={{ minHeight: "100px" }}
          />
        </div>

      </div>
    </Modal.Body>

    <Modal.Footer>
      <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
        Cancel
      </button>
      <button type="submit" className="btn btn-primary">
        Update Requirement
      </button>
    </Modal.Footer>
  </form>
</Modal>


<Modal
  show={showViewDetailsModal}
  onHide={handleCloseViewDetailsModal}
  centered
  size="lg"
  scrollable
  backdrop="static"
  keyboard={false}
>
  <Modal.Header closeButton>
    <Modal.Title>
      Job Requirement Details{" "}
      <small>
        {viewJobData?.createdAt ? `(${formatDate(viewJobData.createdAt)})` : ""}
      </small>
    </Modal.Title>
  </Modal.Header>

  <Modal.Body>
    {isViewLoading && (
      <div className="text-center py-4">
        <p className="text-muted">Loading...</p>
      </div>
    )}

    {!isViewLoading && viewError && (
      <div className="text-center py-4">
        <p className="text-danger">{viewError}</p>
      </div>
    )}

    {!isViewLoading && !viewError && viewJobData && (
      <div className="job-details">
        <div className="row m-0">

          {/* Hospital */}
          <div className="col-md-6 p-2">
            <label className="form-label mb-1">Hospital</label>
            <p className="form-control m-0">
              {viewJobData.hospitalName || getHospitalName(viewJobData.hospitalID)}
            </p>
          </div>

          {/* Looking For */}
          <div className="col-md-6 p-2">
            <label className="form-label mb-1">Looking For</label>
            <p className="form-control m-0">
              {getProfessionName(viewJobData.idProfessionType)}
            </p>
          </div>

          {/* Qualification */}
          <div className="col-md-6 p-2">
            <label className="form-label mb-1">Qualification</label>
            <p className="form-control m-0">
              {getQualificationName(viewJobData.idProfessionType, viewJobData.idQualification)}
            </p>
          </div>

          {/* Experience */}
          <div className="col-md-6 p-2">
            <label className="form-label mb-1">Average Years of Experience</label>
            <p className="form-control m-0">{viewJobData.experienceInYears ?? "-"}</p>
          </div>

          {/* Period */}
          <div className="col-md-6 p-2">
            <label className="form-label mb-1">Period</label>
            <p className="form-control m-0">{viewJobData.jobDurationType || "-"}</p>
          </div>

          {/* Start Date */}
          <div className="col-md-3 p-2">
            <label className="form-label mb-1">Start Date</label>
            <p className="form-control m-0">{formatDate(viewJobData.dateFrom)}</p>
          </div>

          {/* End Date */}
          {viewJobData.jobDurationType === "for a period" && (
            <div className="col-md-3 p-2">
              <label className="form-label mb-1">End Date</label>
              <p className="form-control m-0">{formatDate(viewJobData.dateTo)}</p>
            </div>
          )}

          {/* Shift Only Section */}
          {viewJobData.jobDurationType === "for a shift" && (
            <>
              <div className="col-md-3 p-2">
                <label className="form-label mb-1">Start Time</label>
                <p className="form-control m-0">{viewJobData.timeFrom || "-"}</p>
              </div>

              <div className="col-md-3 p-2">
                <label className="form-label mb-1">Duration (Hours)</label>
                <p className="form-control m-0">{viewJobData.durationInHours ?? "-"}</p>
              </div>

              <div className="col-md-3 p-2">
                <label className="form-label mb-1">Hourly Amount</label>
                <p className="form-control m-0">
                  {viewJobData.hourlyRateByHospital !== null &&
                  viewJobData.hourlyRateByHospital !== undefined
                    ? `Rs. ${viewJobData.hourlyRateByHospital}`
                    : "-"}
                </p>
              </div>
            </>
          )}

          {/* Total Amount (System Calculated) */}
          <div className="col-md-6 p-2">
            <label className="form-label mb-1">Total Amount</label>
            <p className="form-control m-0">
              {viewJobData.jobDurationType === "for a shift" &&
              viewJobData.durationInHours &&
              viewJobData.hourlyRateByHospital
                ? `Rs. ${viewJobData.durationInHours * viewJobData.hourlyRateByHospital}`
                : viewJobData.periodTotalAmountByHospital !== null &&
                  viewJobData.periodTotalAmountByHospital !== undefined
                ? `Rs. ${viewJobData.periodTotalAmountByHospital}`
                : "-"}
            </p>
          </div>

          {/* ✅ PUBLISHED FIELDS SECTION */}
          {viewJobData.jobDurationType === "for a shift" && (
            <>
              <div className="col-md-6 p-2">
                <label className="form-label mb-1">Published Hourly Rate</label>
                <p className="form-control m-0">
                  {viewJobData.publishedHourlyRate !== null &&
                  viewJobData.publishedHourlyRate !== undefined
                    ? `Rs. ${viewJobData.publishedHourlyRate}`
                    : "-"}
                </p>
              </div>

              <div className="col-md-6 p-2">
                <label className="form-label mb-1">Published Period Total Amount</label>
                <p className="form-control m-0">
                  {viewJobData.publishedPeriodTotalAmount !== null &&
                  viewJobData.publishedPeriodTotalAmount !== undefined
                    ? `Rs. ${viewJobData.publishedPeriodTotalAmount}`
                    : "-"}
                </p>
              </div>
            </>
          )}

          {viewJobData.jobDurationType === "for a period" && (
            <div className="col-md-6 p-2">
              <label className="form-label mb-1">Published Period Total Amount</label>
              <p className="form-control m-0">
                {viewJobData.publishedPeriodTotalAmount !== null &&
                viewJobData.publishedPeriodTotalAmount !== undefined
                  ? `Rs. ${viewJobData.publishedPeriodTotalAmount}`
                  : "-"}
              </p>
            </div>
          )}

          {/* Job Details - Half Width */}
          <div className="col-md-6 p-2">
            <label className="form-label mb-1">Job Details</label>
            <div
              className="form-control"
              style={{ minHeight: "80px", whiteSpace: "pre-wrap" }}
              dangerouslySetInnerHTML={{ __html: viewJobData.remarks || "-" }}
            />
          </div>

          {/* Professional Selection - Half Width - Searchable Dropdown */}
          <div className="col-md-6 p-2">
            <label className="form-label mb-1">Select Professional </label>
            <div className="position-relative" ref={professionalDropdownRef}>
              <div className="input-group">
                <span className="input-group-text">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search professional by name..."
                  value={
                    selectedProfessionalId
                      ? professionals.find((p) => p.professionalId === selectedProfessionalId)
                        ? `${professionals.find((p) => p.professionalId === selectedProfessionalId).professionalName}${professionals.find((p) => p.professionalId === selectedProfessionalId).districtName ? ` (${professionals.find((p) => p.professionalId === selectedProfessionalId).districtName})` : ''}`
                        : professionalSearchTerm
                      : professionalSearchTerm
                  }
                  onChange={(e) => {
                    setProfessionalSearchTerm(e.target.value);
                    setSelectedProfessionalId(null);
                    setShowProfessionalDropdown(true);
                  }}
                  onFocus={() => setShowProfessionalDropdown(true)}
                  disabled={isLoadingProfessionals || !viewJobData?.idProfessionType || String(viewJobData?.jobStatus || '').toLowerCase() === 'assigned' || String(viewJobData?.jobStatus || '').toLowerCase() === 'completed'}
                />
                {selectedProfessionalId && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSelectedProfessionalId(null);
                      setProfessionalSearchTerm('');
                    }}
                    title="Clear selection"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Dropdown List */}
              {showProfessionalDropdown && !isLoadingProfessionals && viewJobData?.idProfessionType && (
                <div
                  className="position-absolute w-100 bg-white border rounded mt-1 shadow-lg"
                  style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}
                >
                  {professionals
                    .filter((prof) => {
                      if (!professionalSearchTerm) return true;
                      const searchLower = professionalSearchTerm.toLowerCase();
                      const nameMatch = (prof.professionalName || '').toLowerCase().includes(searchLower);
                      const districtMatch = (prof.districtName || '').toLowerCase().includes(searchLower);
                      return nameMatch || districtMatch;
                    })
                    .length === 0 ? (
                    <div className="p-2 text-muted text-center">No professionals found</div>
                  ) : (
                    professionals
                      .filter((prof) => {
                        if (!professionalSearchTerm) return true;
                        const searchLower = professionalSearchTerm.toLowerCase();
                        const nameMatch = (prof.professionalName || '').toLowerCase().includes(searchLower);
                        const districtMatch = (prof.districtName || '').toLowerCase().includes(searchLower);
                        return nameMatch || districtMatch;
                      })
                      .map((prof) => (
                        <div
                          key={prof.professionalId}
                          className="p-2 border-bottom cursor-pointer"
                          style={{
                            cursor: 'pointer',
                            backgroundColor: selectedProfessionalId === prof.professionalId ? '#e7f3ff' : 'white',
                          }}
                          onClick={() => {
                            setSelectedProfessionalId(prof.professionalId);
                            setProfessionalSearchTerm('');
                            setShowProfessionalDropdown(false);
                          }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = selectedProfessionalId === prof.professionalId ? '#e7f3ff' : 'white')}
                        >
                          {prof.professionalName}
                          {prof.districtName ? <span className="text-muted"> ({prof.districtName})</span> : ''}
                        </div>
                      ))
                  )}
                </div>
              )}
              
              {isLoadingProfessionals && (
                <small className="form-text text-muted d-block mt-1">Loading professionals...</small>
              )}
              {!isLoadingProfessionals && professionals.length === 0 && viewJobData?.idProfessionType && (
                <small className="form-text text-muted d-block mt-1">No professionals found for this profession type.</small>
              )}
            </div>
          </div>

          {/* ✅ Is Published Checkbox */}
<div className="col-md-12 p-2">
  <div className="form-check d-flex align-items-center gap-2">
    <input
      className="form-check-input"
      type="checkbox"
      id="isPublishedCheck"
      checked={isPublishedChecked}
      onChange={(e) => setIsPublishedChecked(e.target.checked)}
      disabled={String(viewJobData?.jobStatus || '').toLowerCase() === 'assigned' || String(viewJobData?.jobStatus || '').toLowerCase() === 'completed'}
    />
    <label className="form-check-label fw-semibold" htmlFor="isPublishedCheck">
      Is Published
    </label>
  </div>

  <small className="text-muted d-block mt-1">
    If checked, job will be published and visible for candidates.
  </small>
</div>

        </div>
      </div>
    )}
  </Modal.Body>

  <Modal.Footer>
  <button
    type="button"
    className="btn btn-secondary"
    onClick={handleCloseViewDetailsModal}
  >
    Close
  </button>
  {viewJobData?.jobStatus === "Pending" && (
  <>
    <button
      type="button"
      className="btn btn-success"
      onClick={() => handleApproveJob(viewingDetailsJobId, "Open")}
      disabled={isViewLoading}
    >
      Approve
    </button>

    <button
      type="button"
      className="btn btn-warning"
      onClick={() => handleApproveJob(viewingDetailsJobId, "Suspended")}
      disabled={isViewLoading}
    >
      Suspend
    </button>
  </>
)}

  {/* Existing Edit Button */}
  {viewingDetailsJobId && viewJobData?.jobStatus === "Pending" && (
    <button
      type="button"
      className="btn btn-primary"
      onClick={() => {     
        handleCloseViewDetailsModal();
        handleEditJob(viewingDetailsJobId);
      }}
    >
      Edit
    </button>
  )}
</Modal.Footer>

</Modal>

      {/* Unassign Professional Modal */}
      <Modal
        show={showUnassignModal}
        onHide={handleCloseUnassignModal}
        size="md"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Unassign Professional</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {unassignJob && (
            <>
              <p>
                Job: <strong>{getJobSummary(unassignJob) || unassignJob.jobDetails || unassignJob.professionTypeName || 'N/A'}</strong>
              </p>
              <div className="mb-3">
                <label className="form-label">Select Replacement (optional)</label>
                {/* search dropdown replicating view-details behavior */}
                <div className="position-relative" ref={unassignDropdownRef}>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search professional by name..."
                      value={
                        selectedUnassignProfessionalId
                          ? professionals.find((p) => p.professionalId === selectedUnassignProfessionalId)
                            ? `${professionals.find((p) => p.professionalId === selectedUnassignProfessionalId).professionalName}${professionals.find((p) => p.professionalId === selectedUnassignProfessionalId).districtName ? ` (${professionals.find((p) => p.professionalId === selectedUnassignProfessionalId).districtName})` : ''}`
                            : unassignSearchTerm
                          : unassignSearchTerm
                      }
                      onChange={(e) => {
                        setUnassignSearchTerm(e.target.value);
                        setSelectedUnassignProfessionalId(null);
                        setUnassignShowDropdown(true);
                      }}
                      onFocus={() => setUnassignShowDropdown(true)}
                      disabled={isLoadingProfessionals}
                    />
                    {selectedUnassignProfessionalId && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setSelectedUnassignProfessionalId(null);
                          setUnassignSearchTerm('');
                        }}
                        title="Clear selection"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {/* Dropdown List (no outside-click close) */}
                  {unassignShowDropdown && !isLoadingProfessionals && professionals.length > 0 && (
                    <div
                      className="position-absolute w-100 bg-white border rounded mt-1 shadow-lg"
                      style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}
                    >
                      {professionals
                        .filter((prof) => {
                          if (!unassignSearchTerm) return true;
                          const searchLower = unassignSearchTerm.toLowerCase();
                          const nameMatch = (prof.professionalName || '').toLowerCase().includes(searchLower);
                          const districtMatch = (prof.districtName || '').toLowerCase().includes(searchLower);
                          return nameMatch || districtMatch;
                        })
                        .map((prof) => (
                          <div
                            key={prof.professionalId}
                            className="p-2 border-bottom"
                            style={{
                              cursor: 'pointer',
                              backgroundColor: selectedUnassignProfessionalId === prof.professionalId ? '#e7f3ff' : 'white',
                            }}
                            onClick={() => {
                              setSelectedUnassignProfessionalId(prof.professionalId);
                              setUnassignSearchTerm('');
                              setUnassignShowDropdown(false);
                            }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                            onMouseLeave={(e) =>
                              (e.target.style.backgroundColor =
                                selectedUnassignProfessionalId === prof.professionalId ? '#e7f3ff' : 'white')
                            }
                          >
                            {prof.professionalName}
                            {prof.districtName ? <span className="text-muted"> ({prof.districtName})</span> : ''}
                          </div>
                        ))}
                    </div>
                  )}

                  {isLoadingProfessionals && <small className="text-muted">Loading professionals...</small>}
                  {!isLoadingProfessionals && professionals.length === 0 && (
                    <small className="form-text text-muted d-block mt-1">No professionals found for this profession type.</small>
                  )}
                </div>
              </div>
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="confirmUnassign"
                  checked={confirmUnassign}
                  onChange={(e) => setConfirmUnassign(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="confirmUnassign">
                  I confirm I want to unassign the current professional
                </label>
              </div>

              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="unassignPublished"
                  checked={unassignPublished}
                  onChange={(e) => setUnassignPublished(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="unassignPublished">
                  Is Published
                </label>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleCloseUnassignModal} disabled={isUnassigning}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleUnassignSubmit} disabled={isUnassigning}>
            {isUnassigning ? 'Saving...' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}

export default PostingByHospitals;

